import {useCallback, useMemo, useState} from 'react';
import {type LayoutChangeEvent, Platform, StyleSheet, useWindowDimensions} from 'react-native';
import {useAnimatedStyle} from 'react-native-reanimated';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useHeaderHeight} from '@react-navigation/elements';

import {useBand} from '../../components/SiteHeader/breakpoints';
import {isSiteHeaderActive} from '../../components/SiteHeader/headerDenyList';
import {heightsFor} from '../../components/SiteHeader/headerGeometry';
import {useOptionalScrollCollapseContext} from '../../context/ScrollCollapseContext';

/**
 * Geometry for a product list under a scroll-collapse route header (`header(…, {scrollCollapse:
 * true})` + `headerTransparent: true`). The screen renders:
 *
 *   <View style={listWrapperStyle} onLayout={onListWrapperLayout}>
 *     <Animated.View style={[wrapperStaticStyle, wrapperAnimatedStyle]}>
 *       <View style={spacerStyle} />  … pill bar …  <FlashList contentContainerStyle={{paddingBottom: maxCollapse}} />
 *
 * and wires `useScrollCollapseJSHandler(listRef)` as the FlashList `onScroll`. Used by
 * RangeProductListScreen and JoinedDepartmentsScreen.
 */
export function useCollapsingListLayout() {
  const headerHeight = useHeaderHeight();
  const insets = useSafeAreaInsets();
  const {height: windowHeight} = useWindowDimensions();
  const scrollCollapseCtx = useOptionalScrollCollapseContext();
  const siteHeaderActive = isSiteHeaderActive();
  const band = useBand();

  // DIS-1127: Measure the actual clip-container height at runtime. The
  // Animated.View wrapper must match this exactly plus maxCollapse so that
  // at max collapse its bottom aligns with listWrapper's bottom (the
  // overflow:hidden clip line). Using `windowHeight` instead — as the
  // previous code did — over-extends the wrapper by `windowHeight -
  // listWrapperHeight`, which on devices with a flex-laid-out native tab
  // bar + home-indicator safe area is ~106 px. That overflow is the exact
  // region FlashList's flex bottom falls into, hiding the last row's
  // "Add to cart" button.
  //
  // Measured value propagates via React state; initial render falls back to
  // `windowHeight` (the old over-extended value) so the wrapper is at least
  // as tall as the clip on the first paint before onLayout fires.
  const [listWrapperHeight, setListWrapperHeight] = useState(0);
  const onListWrapperLayout = useCallback((e: LayoutChangeEvent) => {
    setListWrapperHeight(e.nativeEvent.layout.height);
  }, []);

  // DIS-1127: Wrapper-translate architecture.
  //
  // History: the previous "absolute-positioned pill bar overlay" approach hit
  // the same class of Android native z-order bug that the existing code
  // comments at `StickyFilterBar.tsx:29-32` warn about — product tiles with
  // implicit elevation render ABOVE any absolute overlay that shares their
  // parent, defeating zIndex and `elevation` alike.
  //
  // Fix: flex-sibling layout (spacer → pill bar → FlashList) wrapped in ONE
  // Animated.View whose translateY follows header collapse. Tiles stay
  // naturally clipped inside FlashList's flex bounds (no overlay to leak
  // around), and the whole subtree slides up together as a unit.
  //
  // Geometry: at rest the wrapper extends from y=0 down; at max collapse it
  // has translated up by `maxCollapse`, so its bottom should reach exactly
  // the listWrapper's `overflow: hidden` clip line — no further, no shorter.
  //
  // DIS-1840: with SiteHeader active (web) the header box does not move, and the wrapper follows
  // `siteHeaderCompactOffset` instead, whose maximum is `full − compact`. Size to the ecom value,
  // the largest offset for the band: nonEcom's offset is 0 (ruling R2), and because the overhang
  // and `paddingBottom` below are the same `maxCollapse`, the last row still reaches the clip line
  // at any offset in [0, maxCollapse]. Native and HEADER-denied web keep the formula below.
  const siteHeaderHeights = heightsFor(band, 'ecom');
  const maxCollapse = siteHeaderActive
    ? siteHeaderHeights.full - siteHeaderHeights.compact
    : Math.max(0, headerHeight - insets.top);
  const wrapperHeight = (listWrapperHeight || windowHeight) + maxCollapse;

  const wrapperAnimatedStyle = useAnimatedStyle(() => {
    'worklet';
    if (!scrollCollapseCtx) return {transform: [{translateY: 0}]};
    if (siteHeaderActive) {
      return {transform: [{translateY: -scrollCollapseCtx.siteHeaderCompactOffset.get()}]};
    }
    const ctxMaxCollapse = Math.max(0, scrollCollapseCtx.headerHeight.get() - insets.top);
    const offset = Math.min(scrollCollapseCtx.scrollY.get(), ctxMaxCollapse);
    return {transform: [{translateY: -offset}]};
  });

  const wrapperStaticStyle = useMemo(() => ({height: wrapperHeight}), [wrapperHeight]);

  const spacerStyle = useMemo(() => ({height: headerHeight}), [headerHeight]);

  // Uncollapse the header: the caller scrolls its list to the top alongside this when the list
  // contents change (filter, sort, search).
  const resetScrollCollapse = useCallback(() => {
    if (scrollCollapseCtx) {
      scrollCollapseCtx.scrollY.set(0);
      scrollCollapseCtx.prevScrollY.set(0);
      scrollCollapseCtx.accumulatedUp.set(scrollCollapseCtx.TAB_BAR_HEIGHT);
      scrollCollapseCtx.accumulatedDown.set(0);
    }
  }, [scrollCollapseCtx]);

  // DIS-1127 short-content fix: the Animated.View wrapper is sized to
  // `listWrapperHeight + maxCollapse`, so FlashList (flex:1 inside) gets that
  // same extra `maxCollapse` in height. At rest only `listWrapperHeight`
  // minus header+pill is actually visible on screen — the bottom `maxCollapse`
  // px live behind the listWrapper's `overflow:hidden` clip line until the
  // wrapper translates up on collapse. For content tall enough to overflow
  // the visible-at-rest area but NOT tall enough to exceed FlashList's
  // full viewport (e.g. 2–3 tiles on a small search result), FlashList
  // thinks content fits, never reports a scrollable region, the wrapper
  // never translates, and the last row's bottom stays behind the clip —
  // visible as a clipped "Add to cart" button.
  //
  // Padding the contentContainer by `maxCollapse` inflates the scroll
  // content so any content ≥ visible-at-rest triggers scroll, which
  // collapses the header and exposes the clipped region. Short content
  // (fits at rest) is unaffected — the padding just becomes trailing
  // empty space below the last tile that the user never scrolls into.
  // Callers add `paddingBottom: maxCollapse` to the FlashList contentContainerStyle.
  return {
    listWrapperStyle: styles.listWrapper,
    onListWrapperLayout,
    wrapperStaticStyle,
    wrapperAnimatedStyle,
    spacerStyle,
    maxCollapse,
    resetScrollCollapse,
  };
}

const styles = StyleSheet.create({
  listWrapper: {
    flex: 1,
    // DIS-1127: the wrapper Animated.View is sized to listWrapper height +
    // maxCollapse (see wrapperHeight above). overflow:hidden clips the
    // maxCollapse slack at rest; at max collapse the wrapper has translated
    // up by that same amount, so its content reaches exactly the clip line.
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({contain: 'layout style'} as const) : {}),
  },
});
